// Pre-rendered-from-3D sprite path (Road 2). Loads the per-faction atlas PNGs +
// JSON manifest baked by tools/sprites/gen_units.py and draws a unit by snapping
// its heading to the nearest of 8 pre-rendered yaw frames. The frames were
// rendered straight top-down under a FIXED sun while the hull rotated, so the
// light stays put as the unit turns — that is the fidelity win over vector art.
//
// Everything here is render-side only and same-origin (public/sprites/). If the
// manifest or an atlas fails to load, `ready` stays false and the renderer falls
// back to the procedural vector painters — the game never breaks without sprites.

export interface FrameRect { x: number; y: number; w: number; h: number; }

export interface SpriteUnit {
  faction: string;
  role: string;
  atlas: string;       // faction key into the atlas image table
  row: number;
  frames: FrameRect[]; // one per yaw (length === manifest.yaws)
  spanWorld: number;   // world-units the frame spans (drives on-screen scale)
  portrait: string;    // path (relative to sprites/) of the HUD beauty shot
}

export interface SpriteManifest {
  generated?: string;
  engine?: string;
  frameSize: number;
  yaws: number;
  portraitSize: number;
  atlases: Record<string, string>;
  units: Record<string, SpriteUnit>;
}

const TAU = Math.PI * 2;

export class SpriteAtlas {
  manifest: SpriteManifest | null = null;
  ready = false;                    // manifest + every atlas image decoded
  failed = false;
  private images: Record<string, HTMLImageElement> = {};
  private portraits: Record<string, HTMLImageElement> = {};
  private base: string;
  private loading: Promise<boolean> | null = null;

  constructor(base?: string) {
    // Vite serves public/ at BASE_URL; keep it same-origin and relative.
    const b = base ?? ((import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "./");
    this.base = (b.endsWith("/") ? b : b + "/") + "sprites/";
  }

  /** Idempotent: kick off (or await) the load. Resolves to `ready`. */
  load(): Promise<boolean> {
    if (this.loading) return this.loading;
    this.loading = this.doLoad();
    return this.loading;
  }

  private async doLoad(): Promise<boolean> {
    try {
      const res = await fetch(this.base + "manifest.json", { cache: "force-cache" });
      if (!res.ok) throw new Error("manifest " + res.status);
      const m = (await res.json()) as SpriteManifest;
      if (!m || !m.units || !m.atlases) throw new Error("manifest shape");
      await Promise.all(
        Object.entries(m.atlases).map(([key, file]) =>
          this.loadImage(this.base + file).then((img) => { this.images[key] = img; })),
      );
      this.manifest = m;
      this.ready = true;
      this.preloadPortraits(); // fire-and-forget; HUD selection panel uses these
      return true;
    } catch (err) {
      // Missing/failed atlas is non-fatal: renderer keeps drawing vectors.
      this.failed = true;
      this.ready = false;
      // eslint-disable-next-line no-console
      console.info("[sprites] pre-rendered atlas unavailable, using vector art:", (err as Error)?.message);
      return false;
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("image " + url));
      img.src = url;
    });
  }

  /** True if this unit id has a baked sprite ready to draw. */
  has(unitId: string): boolean {
    if (!this.ready || !this.manifest) return false;
    const u = this.manifest.units[unitId];
    return !!u && !!this.images[u.atlas];
  }

  /** Nearest of the `yaws` pre-rendered frames for a heading (radians). */
  private frameIndex(heading: number): number {
    const n = this.manifest!.yaws;
    let i = Math.round((heading / TAU) * n);
    i = ((i % n) + n) % n;
    return i;
  }

  /**
   * Draw a unit sprite centred at screen (sx, sy), snapped to the nearest yaw
   * frame for `heading`, scaled by `zoom`. The frame already encodes the hull
   * rotation, so the caller must NOT rotate the context. Returns false (drawing
   * nothing) if no baked sprite exists — the caller should then draw a vector.
   */
  drawUnit(
    ctx: CanvasRenderingContext2D,
    unitId: string,
    sx: number,
    sy: number,
    heading: number,
    zoom: number,
    alpha = 1,
  ): boolean {
    if (!this.ready || !this.manifest) return false;
    const u = this.manifest.units[unitId];
    if (!u) return false;
    const img = this.images[u.atlas];
    if (!img) return false;
    const fr = u.frames[this.frameIndex(heading)];
    const size = u.spanWorld * zoom;
    const half = size / 2;
    const prevSmooth = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = true;
    if (alpha !== 1) ctx.globalAlpha = alpha;
    ctx.drawImage(img, fr.x, fr.y, fr.w, fr.h, sx - half, sy - half, size, size);
    if (alpha !== 1) ctx.globalAlpha = 1;
    ctx.imageSmoothingEnabled = prevSmooth;
    return true;
  }

  /** Warm the HUD portrait cache so selection panels have art immediately. */
  private preloadPortraits(): void {
    if (!this.manifest) return;
    for (const id of Object.keys(this.manifest.units)) this.portrait(id);
  }

  /** HUD portrait image for a unit, or null if not baked / not loaded yet. */
  portrait(unitId: string): HTMLImageElement | null {
    if (!this.manifest) return null;
    const u = this.manifest.units[unitId];
    if (!u) return null;
    let img = this.portraits[unitId];
    if (!img) {
      img = new Image();
      img.src = this.base + u.portrait;
      this.portraits[unitId] = img;
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }
}

// App-wide singleton, mirroring `settings` / `audio`.
export const spriteAtlas = new SpriteAtlas();
